import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import {
	formatContractedKoreanBusinessList,
	IPayloadGetContractedKoreanBusinessListRequest,
} from '../foreign-dashboard';
import {
	formatForeignSystemUnitDetail,
	formatForeignSystemUnitList,
	formatHistoryUpdateForeignUnitList,
} from './unit.helper';
import { ForeignSystemUnitActions } from './unit.redux';
import {
	ICreateForeignUnitRequest,
	IPayloadDeleteForeignUnitRequest,
	IPayloadGetForeignUnitDetailRequest,
	IPayloadGetForeignUnitListRequest,
	TPayloadGetHistoryUpdateForeignUnitListRequest,
} from './unit.type';

export function* getForeignSystemUnitList(
	action: PayloadAction<IPayloadGetForeignUnitListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.TGetForeignSystemUnitListResponse> =
			yield API.getForeignSystemUnitList({
				searchTypes,
				...rest,
			});
		const unitList = formatForeignSystemUnitList(res?.data?.content);

		yield put(
			ForeignSystemUnitActions.getSystemUnitListSuccess({
				...rest,
				unitList,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(ForeignSystemUnitActions.getSystemUnitListFailure());
	}
}

export function* createForeignSystemUnit(
	action: PayloadAction<ICreateForeignUnitRequest>
) {
	const { values, callback } = action.payload;

	try {
		yield API.createForeignSystemUnit(values);

		yield put(ForeignSystemUnitActions.createSystemUnitSuccess());
		callback?.();
	} catch (error: unknown) {
		callback?.(error as API.ApiResponseError);
		yield put(ForeignSystemUnitActions.createSystemUnitFailure());
	}
}

export function* getForeignSystemUnitDetail(
	action: PayloadAction<IPayloadGetForeignUnitDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.TForeignSystemUnitItem> =
			yield API.getForeignSystemUnitDetail(id);
		const unitDetail = formatForeignSystemUnitDetail(res?.data);
		yield put(
			ForeignSystemUnitActions.getSystemUnitDetailSuccess({
				unitDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(ForeignSystemUnitActions.getSystemUnitDetailFailure());
	}
}

export function* deleteForeignSystemUnit(
	action: PayloadAction<IPayloadDeleteForeignUnitRequest>
) {
	const { ids, callback } = action.payload;

	try {
		yield API.deleteForeignSystemUnit(ids);

		yield put(ForeignSystemUnitActions.deleteSystemUnitSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(ForeignSystemUnitActions.deleteSystemUnitFailure());
	}
}

export function* getForeignSystemHistoryUpdateSystemUnitList(
	action: PayloadAction<TPayloadGetHistoryUpdateForeignUnitListRequest>
) {
	const { page, pageSize, field, unitId, callback } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetSystemHistoryUpdateUnitListResponse> =
			yield API.getHistoryUpdateSystemUnitList({
				page,
				pageSize,
				field,
				unitId,
			});
		const historyUpdateUnitList = formatHistoryUpdateForeignUnitList(
			res?.data?.content
		);

		yield put(
			ForeignSystemUnitActions.getSystemHistoryUpdateSystemUnitListSuccess({
				page,
				pageSize,
				historyUpdateUnitList,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error as API.ApiResponseError);
		yield put(
			ForeignSystemUnitActions.getSystemHistoryUpdateSystemUnitListFailure()
		);
	}
}

export function* getContractedKoreanBusinessSystem(
	action: PayloadAction<IPayloadGetContractedKoreanBusinessListRequest>
) {
	const { callback, ...restParams } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetContractedKoreanBusinessResponse> =
			yield API.getContractedKoreanBusinessSystem(restParams);

		const data = formatContractedKoreanBusinessList(res?.data);
		const total = res?.data?.total;

		yield put(
			ForeignSystemUnitActions.getContractedKoreanBusinessListSuccess({
				content: data?.content,
				total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			ForeignSystemUnitActions.getContractedKoreanBusinessListFailure()
		);
	}
}
